import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import { RootState } from "..";
import { IStoreItem } from "../../interfaces/IStoredItem";
import { typedAction } from "../helpers";
import { ISuggestedItems } from "../models/reduxModels";
import {
  RESET_USER_SUGGESTED_ITEMS,
  SET_USER_SUGGESTED_ITEMS,
  SET_SUGGESTED_IS_LOADING,
} from "./actionTypes";
import { StoreItemHelper } from "../../components/helpers/storeItemHelper";
import { logger } from "../../../../utilities/logger";
import LZString = require("lz-string");
import { apiGetCompressedSuggestedData, apiSetSuggestedItems } from "../../services/ApiService";
import { ModeEnum } from "../../components/enums/modeEnum";
import { OutlookUtils } from "../../../../utilities/OutlookUtils";

const initialState: ISuggestedItems = {
  isSuggestedItemsLoading: false,
  suggestedItems: null
};

const setIsSuggestedItemsLoading = (isSuggestedItemsLoading: boolean) => {
  return typedAction(SET_SUGGESTED_IS_LOADING, isSuggestedItemsLoading);
};

export const setSuggestedItems = (suggestedItems: IStoreItem[]) => {
  return typedAction(SET_USER_SUGGESTED_ITEMS, StoreItemHelper.normalizeStoreItems(suggestedItems) ?? suggestedItems);
};

export const resetSuggestedItems = () => {
  return typedAction(RESET_USER_SUGGESTED_ITEMS);
};

interface ISuggestedItemResource {
  senders: string[];
  conversationId?: string;
}

interface ISuggestedItemsResponse {
  sender: string;
  conversationId: string;
  encryptedJsonContent?: string;
}

const getSuggestedItemResource = async (mode: ModeEnum, username: string): Promise<ISuggestedItemResource> => {
  try {
    const senders: string[] = mode !== ModeEnum.mailCompose
      ? [Office?.context?.mailbox?.item?.from?.emailAddress ?? username]
      : (await OutlookUtils.getItemTo())?.map(x => x.emailAddress) ?? [username];

    const conversationId: string = Office?.context?.mailbox?.item?.conversationId;

    return { senders: senders.filter(Boolean), conversationId };
  } catch (error) {
    logger(error, "suggestedItems.ts (getSuggestedItemResource)");
  }
  return { senders: [] };
}

export const getSuggestedItems = (accountId: string, mode: ModeEnum, username: string): ThunkAction<Promise<number>, RootState, unknown, Action<string>> => async (dispatch) => {
  dispatch(setIsSuggestedItemsLoading(true));
  let suggestedItemList: IStoreItem[] = [];
  try {
    const { senders, conversationId } = await getSuggestedItemResource(mode, username);

    if (accountId && senders.length > 0) {

      let compressedSuggestedDataList: ISuggestedItemsResponse[] = await apiGetCompressedSuggestedData(accountId, senders, conversationId);
      if (Array.isArray(compressedSuggestedDataList) && compressedSuggestedDataList.length > 0) {

        compressedSuggestedDataList = compressedSuggestedDataList.sort((a, b) => {
          const aIsTargetConversation = a.conversationId === conversationId;
          const bIsTargetConversation = b.conversationId === conversationId;

          if (aIsTargetConversation && !bIsTargetConversation) return -1;
          if (bIsTargetConversation && !aIsTargetConversation) return 1;

          const aIsTargetSender = senders.some(x => x === a.sender);
          const bIsTargetSender = senders.some(x => x === b.sender);

          if (aIsTargetSender && !bIsTargetSender) return -1;
          if (bIsTargetSender && !aIsTargetSender) return 1;

          return 0;
        });

        const suggestedItemMap = new Map<string, IStoreItem>();

        for (const compressedSuggestedData of compressedSuggestedDataList) {
          const decompressedSuggested = LZString.decompressFromBase64(compressedSuggestedData.encryptedJsonContent);
          if (decompressedSuggested) {
            const suggestedItems: IStoreItem[] = JSON.parse(decompressedSuggested);
            if (Array.isArray(suggestedItems)) {
              for (const suggestedItem of suggestedItems) {
                const uniqueKey = `${suggestedItem.driveId}-${suggestedItem.itemId}`;
                if (!suggestedItemMap.has(uniqueKey)) {
                  suggestedItemMap.set(uniqueKey, suggestedItem);
                }
              }
            }
          }
        }

        suggestedItemList = Array.from(suggestedItemMap.values())
          .sort((a, b) => StoreItemHelper.sortByConversationAndDateDesc(a, b, conversationId));

      }
    }
  } catch (error) {
    logger(error, "suggestedItems.ts (getSuggestedItems)");
    suggestedItemList = [];
  }

  if (suggestedItemList.length > 10) {
    suggestedItemList = suggestedItemList
      .slice(0, 10);
  }

  dispatch(setSuggestedItems(suggestedItemList));
  return suggestedItemList.length;
};

export const addSuggestedItem = (accountId: string, mode: ModeEnum, username: string, itemToAdd: IStoreItem): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  const { senders, conversationId } = await getSuggestedItemResource(mode, username);
  if (itemToAdd && accountId && senders.length > 0) {
    let updatedSuggestedItems: IStoreItem[];
    try {
      const normalizedItemToAdd = StoreItemHelper.normalizeStoreItem(itemToAdd);
      const existingSuggestedItems: IStoreItem[] = getState().suggested?.suggestedItems ?? [];

      let itemExists = false;
      updatedSuggestedItems = existingSuggestedItems.map(item => {
        if (StoreItemHelper.itemExists(item, [normalizedItemToAdd])) {
          itemExists = true;
          return { ...item, datePinned: new Date(), conversationId, saveCount: item.saveCount ? item.saveCount + 1 : 2 };
        }
        return { ...item, conversationId };
      });

      if (!itemExists) {
        updatedSuggestedItems.unshift({ ...normalizedItemToAdd, conversationId, saveCount: 1 });
      }

      updatedSuggestedItems.sort(StoreItemHelper.sortByDateDesc);

      apiSetSuggestedItems(accountId, senders, conversationId, updatedSuggestedItems);
      dispatch(setSuggestedItems(updatedSuggestedItems));
    } catch (e) {
      logger(e, "suggestedItems.ts (addSuggestedItem)");
    }
  }
};

export const removeSuggestedItem = (accountId: string, mode: ModeEnum, username: string, item: { itemId: string; driveId: string }): ThunkAction<void, RootState, unknown, Action<string>> => async (dispatch, getState) => {
  try {
    let updatedItems: IStoreItem[];
    const existingSuggestedItems: IStoreItem[] = getState().suggested?.suggestedItems ?? [];

    updatedItems = !item.itemId
      ? existingSuggestedItems.filter(x => x.driveId !== item.driveId || x.itemId != item.itemId)
      : existingSuggestedItems.filter(x => x.itemId !== item.itemId);

    dispatch(setSuggestedItems(updatedItems));

    const { senders, conversationId } = await getSuggestedItemResource(mode, username);

    if (accountId && senders.length > 0) {
      apiSetSuggestedItems(accountId, senders, conversationId, updatedItems);
    }
  } catch (e) {
    logger(e, "suggestedItems.ts (removeSuggestedItem)");
  }
};

type HomeAction = ReturnType<
  | typeof setSuggestedItems
  | typeof resetSuggestedItems
  | typeof setIsSuggestedItemsLoading
>;

export function suggestedReducer(state = initialState, action: HomeAction): ISuggestedItems {
  switch (action.type) {
    case SET_SUGGESTED_IS_LOADING:
      return { ...state, isSuggestedItemsLoading: action.payload };
    case SET_USER_SUGGESTED_ITEMS:
      return { isSuggestedItemsLoading: false, suggestedItems: action.payload };
    case RESET_USER_SUGGESTED_ITEMS:
      return initialState;
    default:
      return state;
  }
}
